import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

export interface RejectionDialogData {
  practitionerName: string;
}

@Component({
  selector: 'app-rejection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon class="warning-icon">warning</mat-icon>
      Reject Practitioner Registration
    </h2>

    <mat-dialog-content>
      <p class="confirmation-text">
        Are you sure you want to reject the registration for
        <strong>{{ data.practitionerName }}</strong>?
      </p>
      <p class="info-text">
        This action cannot be undone and the practitioner record will be permanently deleted.
      </p>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Reason for Rejection</mat-label>
        <textarea
          matInput
          [(ngModel)]="reason"
          placeholder="Please provide a clear reason for rejection..."
          rows="4"
          maxlength="500"
          required
        ></textarea>
        <mat-hint align="end">{{ reason.length }}/500</mat-hint>
      </mat-form-field>

      <p class="note-text">
        <mat-icon>info</mat-icon>
        The practitioner will receive an email with this reason.
      </p>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">
        Cancel
      </button>
      <button
        mat-raised-button
        color="warn"
        (click)="onConfirm()"
        [disabled]="!reason.trim()"
      >
        <mat-icon>block</mat-icon>
        Reject Practitioner
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .warning-icon {
      color: #f44336;
      vertical-align: middle;
      margin-right: 8px;
    }

    .confirmation-text {
      font-size: 16px;
      margin-bottom: 12px;
      color: #333;
    }

    .info-text {
      color: #666;
      font-size: 14px;
      margin-bottom: 20px;
      padding: 12px;
      background-color: #fff3e0;
      border-left: 4px solid #ff9800;
      border-radius: 4px;
    }

    .note-text {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #666;
      font-size: 13px;
      margin-top: 12px;
    }

    .note-text mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: #2196f3;
    }

    .full-width {
      width: 100%;
    }

    mat-dialog-content {
      min-width: 400px;
      max-width: 600px;
    }

    mat-dialog-actions {
      padding: 16px 24px;
      margin: 0;
    }

    strong {
      color: #1976d2;
    }

    textarea {
      font-family: inherit;
    }
  `]
})
export class RejectionDialogComponent {
  reason: string = '';

  constructor(
    public dialogRef: MatDialogRef<RejectionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RejectionDialogData
  ) {}

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.reason.trim()) {
      this.dialogRef.close(this.reason.trim());
    }
  }
}
